import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

// Interfaces para tipado
export interface AgeLimits {
  minAge: number;
  maxAge: number;
  description: string;
  lastUpdated: string;
}

export interface ApprovalGroup {
  id: string;
  name: string;
  description: string;
  minIncome: number;
  maxIncome: number;
  minAge?: number;  // NUEVO: Edad mínima del grupo
  maxAge?: number;  // NUEVO: Edad máxima del grupo
  workingConditions: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApprovalPoliciesResponse {
  ageLimits: AgeLimits;
  approvalGroups: ApprovalGroup[];
  allWorkConditions: string[];
  availableWorkConditions: string[];
  refusedWorkConditions: string[];
  summary: {
    totalGroups: number;
    totalWorkConditions: number;
    totalAvailableWorkConditions: number;
    totalRefusedConditions: number;
    lastUpdated: string;
  };
}

export interface CreateGroupRequest {
  name: string;
  minIncome: number;
  maxIncome: number;
  minAge: number;   // NUEVO
  maxAge: number;   // NUEVO
}

export interface UpdateGroupIncomeRequest {
  name: string;
  minIncome: number;
  maxIncome: number;
}

export interface UpdateGroupAgeRequest {
  name: string;
  minAge: number;
  maxAge: number;
}

export interface AddWorkConditionRequest {
  name: string;
  workCondition: string;
}

export interface UpdateAgePolicyRequest {
  minAge: number;
  maxAge: number;
}

@Injectable({
  providedIn: 'root'
})
export class ApprovalPoliciesService {
  private baseUrl: string;

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.BASE_LA;
  }

  /**
   * Obtener políticas de aprobación completas
   * GET /api/app-config/approval-policies
   */
  getApprovalPolicies = async (): Promise<{ ok: boolean; data: ApprovalPoliciesResponse }> => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/approval-policies`;

    console.log('=== GET APPROVAL POLICIES DEBUG ===');
    console.log('URL:', url);

    try {
      const response = await lastValueFrom(
        this.httpClient.get<any>(url, { headers })
      );

      console.log('Get approval policies response:', response);
      return response;
    } catch (error) {
      console.error('Error getting approval policies:', error);
      throw error;
    }
  };

  /**
   * Crear nuevo grupo de aprobación
   * POST /api/app-config/approval-groups/create
   */
  createApprovalGroup = async (groupData: CreateGroupRequest) => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/approval-groups/create`;

    try {
      const response = await lastValueFrom(
        this.httpClient.post<any>(url, groupData, { headers })
      );

      console.log('Create approval group response:', response);
      return response;
    } catch (error) {
      console.error('Error creating approval group:', error);
      throw error;
    }
  };

  /**
   * Eliminar grupo de aprobación
   * DELETE /api/app-config/approval-groups/delete
   */
  deleteApprovalGroup = async (groupName: string) => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/approval-groups/delete`;

    try {
      const response = await lastValueFrom(
        this.httpClient.delete<any>(url, {
          headers,
          body: { name: groupName }
        })
      );

      console.log('Delete approval group response:', response);
      return response;
    } catch (error) {
      console.error('Error deleting approval group:', error);
      throw error;
    }
  };

  /**
   * Modificar límites de ingresos de grupo
   * PUT /api/app-config/approval-groups/update-income
   */
  updateApprovalGroupIncomeLimits = async (data: UpdateGroupIncomeRequest) => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/approval-groups/update-income`;

    try {
      const response = await lastValueFrom(
        this.httpClient.put<any>(url, data, { headers })
      );

      console.log('Update approval group income limits response:', response);
      return response;
    } catch (error) {
      console.error('Error updating approval group income limits:', error);
      throw error;
    }
  };

  /**
   * =====================================================
   * NUEVO: Modificar límites de edad de grupo
   * PUT /api/app-config/approval-groups/update-age
   * =====================================================
   */
  updateApprovalGroupAgeLimits = async (data: UpdateGroupAgeRequest) => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/approval-groups/update-age`;

    try {
      const response = await lastValueFrom(
        this.httpClient.put<any>(url, data, { headers })
      );

      console.log('Update approval group age limits response:', response);
      return response;
    } catch (error) {
      console.error('Error updating approval group age limits:', error);
      throw error;
    }
  };

  /**
   * Agregar nueva condición laboral
   * POST /api/app-config/work-conditions
   */
  addWorkCondition = async (workCondition: string) => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/work-conditions`;

    try {
      const response = await lastValueFrom(
        this.httpClient.post<any>(url, { workCondition }, { headers })
      );

      console.log('Add work condition response:', response);
      return response;
    } catch (error) {
      console.error('Error adding work condition:', error);
      throw error;
    }
  };

  /**
   * Agregar condición laboral a grupo
   * POST /api/app-config/approval-groups/work-conditions
   */
  addWorkConditionToGroup = async (data: AddWorkConditionRequest) => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/approval-groups/work-conditions`;

    try {
      const response = await lastValueFrom(
        this.httpClient.post<any>(url, data, { headers })
      );

      console.log('Add work condition to group response:', response);
      return response;
    } catch (error) {
      console.error('Error adding work condition to group:', error);
      throw error;
    }
  };

  /**
   * Remover condición laboral de grupo
   * DELETE /api/app-config/approval-groups/work-conditions
   */
  removeWorkConditionFromGroup = async (data: AddWorkConditionRequest) => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/approval-groups/work-conditions`;

    try {
      const response = await lastValueFrom(
        this.httpClient.delete<any>(url, {
          headers,
          body: data
        })
      );

      console.log('Remove work condition from group response:', response);
      return response;
    } catch (error) {
      console.error('Error removing work condition from group:', error);
      throw error;
    }
  };

  /**
   * Agregar condición laboral a lista rechazada
   * POST /api/app-config/refused-work-conditions
   */
  addWorkConditionToRefusedList = async (workCondition: string) => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/refused-work-conditions`;

    try {
      const response = await lastValueFrom(
        this.httpClient.post<any>(url, { workCondition }, { headers })
      );

      console.log('Add work condition to refused list response:', response);
      return response;
    } catch (error) {
      console.error('Error adding work condition to refused list:', error);
      throw error;
    }
  };

  /**
   * Remover condición laboral de lista rechazada
   * DELETE /api/app-config/refused-work-conditions
   */
  removeWorkConditionFromRefusedList = async (workCondition: string) => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/refused-work-conditions`;

    try {
      const response = await lastValueFrom(
        this.httpClient.delete<any>(url, {
          headers,
          body: { workCondition }
        })
      );

      console.log('Remove work condition from refused list response:', response);
      return response;
    } catch (error) {
      console.error('Error removing work condition from refused list:', error);
      throw error;
    }
  };

  /**
   * Actualizar política de edad
   * PUT /api/app-config/age-policy
   */
  updateAgePolicy = async (data: UpdateAgePolicyRequest) => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/age-policy`;

    try {
      const response = await lastValueFrom(
        this.httpClient.put<any>(url, data, { headers })
      );

      console.log('Update age policy response:', response);
      return response;
    } catch (error) {
      console.error('Error updating age policy:', error);
      throw error;
    }
  };

  /**
   * Habilitar/deshabilitar registro de usuarios
   * PUT /api/app-config/register/toggle
   */
  toggleUserRegistration = async (enable: boolean) => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/register/toggle`;

    try {
      const response = await lastValueFrom(
        this.httpClient.put<any>(url, { enable }, { headers })
      );

      console.log('Toggle user registration response:', response);
      return response;
    } catch (error) {
      console.error('Error toggling user registration:', error);
      throw error;
    }
  };

  /**
   * Actualizar mensaje de registro
   * PUT /api/app-config/register/message
   */
  updateRegistrationMessage = async (text: string) => {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.baseUrl}/api/app-config/register/message`;

    try {
      const response = await lastValueFrom(
        this.httpClient.put<any>(url, { text }, { headers })
      );

      console.log('Update registration message response:', response);
      return response;
    } catch (error) {
      console.error('Error updating registration message:', error);
      throw error;
    }
  };

  /**
   * Obtener logs de auditoría con filtros opcionales
   * GET /api/app-config/audit-logs?code=XXX&dateInitial=mm/dd/yyyy&dateFinal=mm/dd/yyyy
   */
  getAuditLogs = async (code?: string, dateInitial?: string, dateFinal?: string): Promise<{ success: boolean; data: any[] }> => {
    const headers = { 'content-type': 'application/json' };
    
    // Construir query params
    let queryParams = '';
    const params: string[] = [];
    
    if (code && code.trim() !== '') {
      params.push(`code=${encodeURIComponent(code.trim())}`);
    }
    
    if (dateInitial && dateInitial.trim() !== '') {
      params.push(`dateInitial=${encodeURIComponent(dateInitial.trim())}`);
    }
    
    if (dateFinal && dateFinal.trim() !== '') {
      params.push(`dateFinal=${encodeURIComponent(dateFinal.trim())}`);
    }
    
    if (params.length > 0) {
      queryParams = '?' + params.join('&');
    }
    
    const url = `${this.baseUrl}/api/app-config/audit-logs${queryParams}`;

    console.log('🔍 DEBUG Servicio: URL completa:', url);
    console.log('🔍 DEBUG Servicio: Parámetros recibidos:', { code, dateInitial, dateFinal });

    try {
      const response = await lastValueFrom(
        this.httpClient.get<any>(url, { headers })
      );

      console.log('Get audit logs response:', response);
      return response;
    } catch (error) {
      console.error('Error getting audit logs:', error);
      throw error;
    }
  };
}
